import { useForm, Controller } from "react-hook-form";
import SelectCountries from "../../components/ui/Selects/Countries";
import FieldError from "../../components/ui/FieldError";
import RequiredField from "../../components/ui/RequiredField";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Base from "../base";
import axiosConfig from "../../services/Axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBadgeCheck } from "@fortawesome/pro-thin-svg-icons";
import {
  faLocationCheck,
  faAddressBook,
  faEye,
  faEyeSlash,
  faHandshake,
} from "@fortawesome/pro-solid-svg-icons";
import PasswordRequirement from "../../components/ui/PasswordRequirement";
import dayjs from "dayjs";
import ReCAPTCHA from "react-google-recaptcha";
import LogoHead from "../../components/LogoHead";
import { pickBy } from "lodash";
import { Link } from "react-router-dom";

function Register() {
  const form = useForm();
  const [successMsg, setSuccessMsg] = useState(false);
  const [country, setCountry] = useState();
  const [passwordShown, setPasswordShown] = useState(false);
  const recaptchaRef = useRef();
  const { t } = useTranslation();

  const setCountryByGeoloc = async () => {
    await fetch(
      "https://api.geoapify.com/v1/ipinfo?apiKey=" +
        process.env.REACT_APP_GEOAPIFY_API_KEY,
      {
        method: "GET",
      }
    )
      .then(function (response) {
        return response.json();
      })
      .then(function (json) {
        setCountry(json.country.iso_code);
      });
  };

  const togglePasswordShown = () => {
    setPasswordShown(!passwordShown);
  };

  useEffect(() => {
    setCountryByGeoloc();
  }, []);

  const onSubmit = async (data) => {
    data.captcha = data.captcha ? data.captcha : "";

    if (data.cguValidationDate) {
      data.cguValidationDate = dayjs().format("YYYY-MM-DD");
    } else {
      delete data.cguValidationDate;
    }

    try {
      data.partnerUser = pickBy(
        data.partnerUser,
        (value) =>
          value !== null &&
          (value > 0 ||
            value.length > 0 ||
            (typeof value === "object" && !Array.isArray(value)))
      );
      data.partnerContacts[0] = pickBy(
        data.partnerContacts[0],
        (value) =>
          value !== null &&
          (value > 0 ||
            value.length > 0 ||
            (typeof value === "object" && !Array.isArray(value)))
      );
      data.partnerLogisticAddresses[0] = pickBy(
        data.partnerLogisticAddresses[0],
        (value) =>
          value !== null &&
          (value > 0 ||
            value.length > 0 ||
            (typeof value === "object" && !Array.isArray(value)))
      );
      const sanitizedData = pickBy(
        data,
        (value) =>
          value !== null &&
          (value > 0 ||
            value.length > 0 ||
            (typeof value === "object" && !Array.isArray(value)))
      );

      await axiosConfig
        .post(process.env.REACT_APP_API_URL + "/api/partners", sanitizedData)
        .then(() => {
          setSuccessMsg(true);
        });
    } catch (e) {
      if (e.response.status !== 200 && e.response.data?.violations) {
        e.response.data.violations.map((v) => {
          form.setError(v.propertyPath, {
            type: "manual",
            message: v.message,
          });
        });

        recaptchaRef.current.reset();
      }
    }
  };

  return (
    <Base navbar={false}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col py-16 justify-center items-center">
          <LogoHead />
          <div className="mt-16 w-full">
            <div className="w-full">
              {!successMsg ? (
                <>
                  <div
                    className={`text-2xl mb-20 bg-white py-8 header-register`}
                  >
                    <div className="text-center flex flex-col items-center justify-center">
                      <FontAwesomeIcon
                        icon={faHandshake}
                        className="text-8xl text-bioport-orange"
                      />
                      <div className="font-bold text-2xl mt-3 py-2 uppercase text-white">
                        {t("login.account.create")}
                      </div>
                    </div>
                  </div>

                  <div className="m-auto w-4/5 lg:w-3/5 xl:w-2/5">
                    <div className="font-bold mb-5 text-2xl">
                      {t("login.account.user_infos")}
                    </div>
                    <div className="mt-2 grid border-t border-gray-400 lg:border-none lg:bg-white p-0 lg:p-10 pt-6 lg:shadow">
                      <label>
                        {t("login.email")} <RequiredField /> :
                      </label>
                      <input
                        {...form.register("partnerUser.email")}
                        name="partnerUser.email"
                        type="text"
                        className="w-full"
                        autoComplete="off"
                      />
                      <FieldError
                        error={form.formState?.errors?.partnerUser?.email}
                      />

                      <label className="mt-6">
                        {t("login.pass")} <RequiredField /> :
                      </label>

                      <div className="relative">
                        <input
                          {...form.register("partnerUser.plainPassword")}
                          name="partnerUser.plainPassword"
                          type={passwordShown ? "text" : "password"}
                          className="w-full"
                          placeholder={t("login.pass")}
                          autoComplete="off"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
                          <FontAwesomeIcon
                            icon={passwordShown ? faEye : faEyeSlash}
                            className={`${
                              passwordShown
                                ? "text-bioport-blue-dark"
                                : "text-gray-400"
                            } cursor-pointer mr-2`}
                            onClick={() => togglePasswordShown()}
                          />
                        </div>
                      </div>
                      <FieldError
                        error={
                          form.formState?.errors?.partnerUser?.plainPassword
                        }
                      />
                      <input
                        {...form.register("partnerUser.password_repeat", {
                          validate: (value) =>
                            value === form.watch("partnerUser.plainPassword") ||
                            t("login.password.repeat_pass"),
                        })}
                        name="partnerUser.password_repeat"
                        type={passwordShown ? "text" : "password"}
                        className="w-full mt-2"
                        placeholder={t("login.password.repeat_pass")}
                        onPaste={(e) => {
                          e.preventDefault();
                          return false;
                        }}
                      />
                      <FieldError
                        error={
                          form.formState?.errors?.partnerUser?.password_repeat
                        }
                      />

                      <PasswordRequirement />
                    </div>

                    <div className="font-bold mb-5 mt-10 text-2xl">
                      {t("login.account.partner_infos")}
                    </div>
                    <div className="mt-2 grid border-t border-gray-400 lg:border-none lg:bg-white p-0 lg:p-10 pt-6 lg:shadow">
                      <div className="col-span-2">
                        <label>
                          {t("partner.raison_soc")} <RequiredField /> :
                        </label>
                        <input
                          {...form.register("name")}
                          name="name"
                          type="text"
                          className="w-full"
                        />
                        <FieldError error={form.formState?.errors?.name} />
                      </div>

                      <label>{t("partner.siren_number")} :</label>
                      <div className="col-span-2">
                        <input
                          {...form.register("sirenNumber")}
                          name="sirenNumber"
                          type="text"
                          className="w-full"
                        />
                        <FieldError
                          error={form.formState?.errors?.sirenNumber}
                        />
                      </div>
                      <div className="col-span-2 lg:col-span-1">
                        <label>{t("partner.naf_code")} :</label>
                        <input
                          {...form.register("nafCode")}
                          name="nafCode"
                          type="text"
                          className="w-full"
                        />
                        <FieldError error={form.formState?.errors?.nafCode} />
                      </div>

                      <div className="col-span-2 lg:col-span-1 pl-0 lg:pl-3">
                        <label>{t("partner.vat_number")} :</label>
                        <input
                          {...form.register("vatNumber")}
                          name="vatNumber"
                          type="text"
                          className="w-full"
                        />
                        <FieldError error={form.formState?.errors?.vatNumber} />
                      </div>

                      <div className="col-span-2">
                        <label>{t("partner.website")} :</label>
                        <input
                          {...form.register("website")}
                          name="website"
                          type="text"
                          className="w-full"
                        />
                        <FieldError error={form.formState?.errors?.website} />
                      </div>

                      <div className="font-bold col-span-2 text-xl mt-6 mb-3">
                        <FontAwesomeIcon
                          icon={faAddressBook}
                          className="mt-5 lg:mt-0 mr-2 text-bioport-orange"
                        />{" "}
                        {t("partnerContact.main")}
                      </div>

                      <div className="col-span-2 bg-gray-100 grid grid-cols-2 w-full lg:p-6 rounded-lg">
                        <div className="col-span-2 lg:col-span-1">
                          <label>
                            {t("user.firstName")} <RequiredField /> :
                          </label>
                          <input
                            {...form.register("partnerContacts[0].firstName")}
                            name="partnerContacts[0].firstName"
                            type="text"
                            className="w-full"
                          />
                          <FieldError
                            error={
                              form.formState?.errors?.partnerContacts &&
                              form.formState?.errors?.partnerContacts[0]
                                ?.firstName
                            }
                          />
                        </div>
                        <div className="col-span-2 lg:pl-3 lg:col-span-1">
                          <label>
                            {t("user.lastName")} <RequiredField /> :
                          </label>
                          <input
                            {...form.register("partnerContacts[0].lastName")}
                            name="partnerContacts[0].lastName"
                            type="text"
                            className="w-full"
                          />
                          <FieldError
                            error={
                              form.formState?.errors?.partnerContacts &&
                              form.formState?.errors?.partnerContacts[0]
                                ?.lastName
                            }
                          />
                        </div>
                        <div className="col-span-2">
                          <label>
                            {t("user.phoneNumber")} <RequiredField /> :
                          </label>
                          <input
                            {...form.register("partnerContacts[0].phoneNumber")}
                            name="partnerContacts[0].phoneNumber"
                            type="text"
                            className="w-full"
                          />
                          <FieldError
                            error={
                              form.formState?.errors?.partnerContacts &&
                              form.formState?.errors?.partnerContacts[0]
                                ?.phoneNumber
                            }
                          />
                        </div>

                        <div className="col-span-2">
                          <label>
                            {t("login.email")} <RequiredField /> :
                          </label>
                          <input
                            {...form.register("partnerContacts[0].email")}
                            name="partnerContacts[0].email"
                            type="text"
                            className="w-full"
                          />
                          <FieldError
                            error={
                              form.formState?.errors?.partnerContacts &&
                              form.formState?.errors?.partnerContacts[0]?.email
                            }
                          />
                        </div>
                      </div>

                      <div className="col-span-2 font-bold text-xl mt-6 mb-3">
                        <FontAwesomeIcon
                          icon={faLocationCheck}
                          className="mt-5 lg:mt-0 lg:mx-2 mr-2 text-bioport-orange"
                        />{" "}
                        {t("partnerLogisticAddress.main_address")}
                      </div>

                      <div className="col-span-2 bg-gray-100 rounded-lg grid grid-cols-2 w-full lg:p-6">
                        <div className="col-span-2">
                          <label>
                            {t("partnerLogisticAddress.address")}{" "}
                            <RequiredField /> :
                          </label>
                          <input
                            {...form.register(
                              "partnerLogisticAddresses[0].address"
                            )}
                            name="partnerLogisticAddresses[0].address"
                            type="text"
                            className="w-full"
                          />
                          <FieldError
                            error={
                              form.formState?.errors
                                ?.partnerLogisticAddresses &&
                              form.formState?.errors
                                ?.partnerLogisticAddresses[0]?.address
                            }
                          />
                        </div>
                        <div className="col-span-2">
                          <label>
                            {t("partnerLogisticAddress.address_complement")} :
                          </label>
                          <input
                            {...form.register(
                              "partnerLogisticAddresses[0].addressComplement"
                            )}
                            name="partnerLogisticAddresses[0].addressComplement"
                            type="text"
                            className="w-full"
                          />
                          <FieldError
                            error={
                              form.formState?.errors
                                ?.partnerLogisticAddresses &&
                              form.formState?.errors
                                ?.partnerLogisticAddresses[0]?.addressComplement
                            }
                          />
                        </div>
                        <div className="col-span-2 lg:col-span-1">
                          <label>
                            {t("partnerLogisticAddress.zipcode")}{" "}
                            <RequiredField /> :
                          </label>
                          <input
                            {...form.register(
                              "partnerLogisticAddresses[0].zipcode"
                            )}
                            name="partnerLogisticAddresses[0].zipcode"
                            type="text"
                            className="w-full"
                          />
                          <FieldError
                            error={
                              form.formState?.errors
                                ?.partnerLogisticAddresses &&
                              form.formState?.errors
                                ?.partnerLogisticAddresses[0]?.zipcode
                            }
                          />
                        </div>

                        <div className="col-span-2 lg:pl-3 lg:col-span-1">
                          <label>
                            {t("partnerLogisticAddress.city")} <RequiredField />{" "}
                            :
                          </label>
                          <input
                            {...form.register(
                              "partnerLogisticAddresses[0].city"
                            )}
                            name="partnerLogisticAddresses[0].city"
                            type="text"
                            className="w-full"
                          />
                          <FieldError
                            error={
                              form.formState?.errors
                                ?.partnerLogisticAddresses &&
                              form.formState?.errors
                                ?.partnerLogisticAddresses[0]?.city
                            }
                          />
                        </div>
                        <div className="col-span-2">
                          <label>
                            {t("partnerLogisticAddress.country")}{" "}
                            <RequiredField /> :
                          </label>
                          {country && (
                            <Controller
                              name="partnerLogisticAddresses[0].country"
                              control={form.control}
                              childRef={form.register}
                              defaultValue={country}
                              render={({ field: { onChange, value } }) => (
                                <SelectCountries
                                  onChange={onChange}
                                  value={value}
                                />
                              )}
                            />
                          )}
                          <FieldError
                            error={
                              form.formState?.errors
                                ?.partnerLogisticAddresses &&
                              form.formState?.errors
                                ?.partnerLogisticAddresses[0]?.country
                            }
                          />
                        </div>
                      </div>

                      <div className="col-span-2 px-3 pt-8 text-sm">
                        <input
                          type="checkbox"
                          className="mr-3"
                          {...form.register("cguValidationDate")}
                        />
                        J'accepte les{" "}
                        <a
                          href="https://www.bioport-logistique.com/fr/mentions-legales/"
                          className="font-bold underline"
                          target="_blank"
                        >
                          conditions générales d'utilisation
                        </a>
                        <RequiredField />
                        <FieldError
                          error={form.formState?.errors?.cguValidationDate}
                        />
                      </div>
                      <div className="p-3 pt-5 col-span-2">
                        <Controller
                          name="captcha"
                          control={form.control}
                          render={({ field: { onChange } }) => (
                            <ReCAPTCHA
                              sitekey={
                                process.env.REACT_APP_GOOGLE_CAPTCHA_KEY ||
                                "key"
                              }
                              onChange={onChange}
                              ref={recaptchaRef}
                            />
                          )}
                        />

                        <FieldError error={form.formState?.errors?.captcha} />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-sm italic">
                        {" "}
                        <RequiredField /> {t("global.required_field")}
                      </div>
                      <div className="pt-3">
                        <Link
                          to={"/"}
                          className="mr-4 text-bioport-orange-dark text-sm hover:underline"
                        >
                          {t("login.sign_in")}
                        </Link>
                        <button className="btn btn-primary" type="submit">
                          {t("login.account.create_label")}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center flex flex-col items-center justify-center mb-10">
                  <FontAwesomeIcon
                    icon={faBadgeCheck}
                    className="text-8xl text-green-600"
                  />
                  <div className="font-bold text-2xl mt-3 py-2">
                    {t("login.account.need_validation")}
                  </div>
                  <div>{t("login.account.need_validation_email")}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </Base>
  );
}

export default Register;
